import { Box, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import ChatInput from "components/ChatInput";
import Main from "layouts/Main";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { answerEndpoint, getChatHistory } from "services/api";
import Message from "./components/Message";
import { useTranslation } from "react-i18next";
import Container from "components/Container";
import { showSnackbar } from "services/snackbarHelper";

const Chatroom = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const { t } = useTranslation(["validation"]);

    const chatRef = useRef(null);
    const uuid = useRef(crypto.randomUUID()).current;

    const [inputValue, setInputValue] = useState("");
    const [dataset, setDataset] = useState(searchParams.get("dataset"));
    const [datasetName, setDatasetName] = useState("");
    const [channel, setChannel] = useState(id || null);
    const [messageList, setMessageList] = useState([]);

    const [isAsking, setIsAsking] = useState(false);

    useEffect(() => {
        const ws = new WebSocket("wss://saasragtest-ws-server.mi-expert.com/ws/llm/" + uuid);

        ws.onopen = () => {
            // ws.send(JSON.stringify({ prompt: "Hello" }));
        };
        var stack="";
        ws.onmessage = (ev) => {
            try {
                const json = JSON.parse(ev.data);

                if (json.event === "external_push") {
                    
                    setMessageList((prevList) => {
                        if (prevList.length === 0) return prevList;
                        const newList = [...prevList];
                        const lastIndex = newList.length - 1;
                        const lastMessage = newList[lastIndex];

                        if (lastMessage.sentBy !== "assistant") return prevList;

                        if (json.message === "LLM Stream Chunk") {
                            const { fullString } = json.data;
                            newList[lastIndex] = {
                                ...lastMessage,
                                message: { messageType: "string", messageContent: fullString },
                                loadingStatus: null
                            };
                        } else if (json.message === "__complete__") {
                            newList[lastIndex] = {
                                ...lastMessage,
                                loadingStatus: null,
                                isStreaming: false
                            };
                            stack="";
                        } else if (typeof json.message === "string") {
                            if(json.message=="Searching for relevant documents for query"){
                                json.message=json.message+" "+json.data.query
                            }
                            stack += json.message + "\n";
                            newList[lastIndex] = {
                                ...lastMessage,
                                loadingStatus: stack
                            };
                        }
                        return newList;
                    });
                }
            } catch (e) {
                console.error("Error parsing WS message:", e);
            }
        };

        return () => {
            if (ws.readyState === 1) ws.close();
        };
    }, [uuid]);

    const handleSubmit = () => {
        const question = inputValue || searchParams.get("question");
        if (!question) return;

        const tempMessageList = [...messageList];
        
        tempMessageList.push({
            sentBy: "user",
            sentTime: new Date().getTime(),
            message: { messageType: "string", messageContent: question },
        });

        tempMessageList.push({
            sentBy: "assistant",
            sentTime: new Date().getTime() + 1,
            message: { messageType: "string", messageContent: "" },
            loadingStatus: "Initializing...", 
            isStreaming: true,
        });

        setInputValue("");
        setMessageList(tempMessageList);
        setIsAsking(true);

        return answerEndpoint(question, dataset, channel, uuid)
            .then((resp) => {
                if (!resp) return;
                if (resp?.error) throw resp;

                chatRef.current && chatRef.current.updateCredit(resp.credit);

                setMessageList((prevList) => {
                    const newList = [...prevList];
                    const lastIndex = newList.length - 1;
                    if (lastIndex >= 0 && newList[lastIndex].sentBy === "assistant") {
                        newList[lastIndex] = {
                            ...newList[lastIndex],
                            message: { messageType: "string", messageContent: resp.answer },
                            contextTitle: resp.contextTitle,
                            loadingStatus: null,
                            isStreaming: false
                        };
                    }
                    return newList;
                });

                if (!channel) {
                    setChannel(resp.channel);
                    navigate(`/app/chat/${resp.channel}`, { replace: true });
                }

                setIsAsking(false);
                return resp;
            })
            .catch((error) => {
                showSnackbar(error.error || "Unknown error", "error");
                setMessageList((prevList) => {
                    const newList = [...prevList];
                    const lastIndex = newList.length - 1;
                    if (lastIndex >= 0) {
                        newList[lastIndex].status = "error";
                        newList[lastIndex].helperText = error.error;
                        newList[lastIndex].loadingStatus = null;
                    }
                    return newList;
                });
                setIsAsking(false);
            });
    };

    React.useEffect(() => {
        if (searchParams.get("question")) handleSubmit();
    }, []);

    React.useEffect(() => {
        if (!channel) return;
        getChatHistory(channel).then((resp) => {
            if (!resp || resp?.error) return;
            chatRef.current && chatRef.current.updateCredit(resp.credit);
            setDataset(resp.datasetInfo.datasetId);
            setDatasetName(resp.datasetInfo.datasetName);
            setMessageList(
                resp.messages.map((message) => ({
                    id: message.id,
                    sentBy: message.sender,
                    sentTime: message.time,
                    message: { messageType: "string", messageContent: message.message },
                    contextTitle: message.contextTitle,
                }))
            );
        });
    }, []);

    // SCROLL LOGIC
    React.useEffect(() => {
        if (messageList.length === 0) return;

        // Only scroll if a NEW message has been added.
        // We do NOT listen to content updates within the same message.
        const lastMsg = messageList[messageList.length - 1];
        const target = document.getElementById(lastMsg.sentTime.toString());
        
        if (target) {
            // "block: 'start'" aligns the top of the new message with the top of the viewport.
            // This is perfect for reading the start of a long answer.
            // Using 'nearest' is also good to just ensure it's visible.
            target.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
    }, [messageList.length]); // <--- DEPENDENCY CHANGED from [messageList] to [messageList.length]

    return (
        <Main title={datasetName}>
            <Box height={1} display={"flex"} flexDirection={"column"}>
                <Box display="flex" flex="1 1 auto" overflow="hidden">
                    <Box flex="1 1 auto" height="100%" sx={{ overflowY: "auto" }}>
                        <Container>
                            {messageList.map((message, index) => {
                                const hasContent = message.message?.messageContent?.length > 0;
                                return (
                                    <Box
                                        key={index}
                                        id={message.sentTime}
                                        my={1}
                                        sx={{ 
                                            display: "flex", 
                                            flexDirection: "column", 
                                            alignItems: message.sentBy === "user" ? "flex-end" : "flex-start" 
                                        }}>
                                        
                                        {(hasContent || message.sentBy !== "assistant") && (
                                            <Message message={message} />
                                        )}
                                        
                                        {message.sentBy === "assistant" && message.loadingStatus && (
                                            <Box 
                                                sx={{ 
                                                    display: "flex", 
                                                    alignItems: "center", 
                                                    gap: 1.5, 
                                                    mt: hasContent ? 1 : 0, 
                                                    ml: 1,
                                                    p: 1,
                                                    borderRadius: 1,
                                                    bgcolor: 'action.hover' 
                                                }}
                                            >
                                                <CircularProgress size={14} thickness={5} />
                                                <Typography 
                                                    variant="body2" 
                                                    color="text.secondary"
                                                    sx={{ whiteSpace: 'pre-line' }}
                                                    >
                                                    {message.loadingStatus}
                                                </Typography>
                                            </Box>
                                        )}

                                        {message?.status === "error" && (
                                            <Typography color="error" sx={{ mx: 1 }}>
                                                {t(message?.helperText || "networkError", { ns: "validation" })}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                        </Container>
                    </Box>
                </Box>

                <Box pt={2} width={1}>
                    <Container>
                        <ChatInput
                            ref={chatRef}
                            handleSubmit={handleSubmit}
                            inputValue={inputValue}
                            setInputValue={setInputValue}
                        />
                    </Container>
                </Box>
            </Box>
        </Main>
    );
};

export default Chatroom;
